import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import ChatChannel, {
  ChannelType,
  CHANNEL_MESSAGE_PAGE_SIZE,
} from '../../Model/ChatChannel';
import Coach from '../../Model/Coach';
import useComponentMounted from '../../hooks/useComponentMounted';
import useStorage, { StorageProcessState } from '../../hooks/useStorage';
import {
  storagePaths,
  pathPlaceholder,
} from '../../utils/firebasePaths';
import { isChatEnabled } from '../../utils/featureToggles';
import { ChannelError } from '../../utils/channelError';
import { ChatPage } from '../../utils/chatPage';
import { getProcessedAttachmentData } from '../../utils/attachments';
import FirebaseContext from '../FirebaseContext';
import ChatContext from '../ChatContext';
import ChatChannelListContext from '../ChatChannelListContext';
import ChannelUnreadCount from '../../Model/ChannelUnreadCount';
import {
  ChatActivity,
  EventType,
  getChatActionType,
} from '../../utils/chatLogging';
import { Event } from '../../utils/events';
import useScheduleQueue from '../../hooks/useScheduleQueue';
import useUnseenThreads from '../../hooks/useUnseenThreads';
import ChatChannelContext, {
  chatChannelContextInitialState as initialState,
} from './ChatChannelContext';

// How few visible messages a fetched page may leave before another page is pulled.
const MIN_VISIBLE_MESSAGES = 15;

const ChatChannelContextProvider = ({
  children,
  navigateToClientPage,
  renderHeaderExtras,
  parentApp,
}) => {
  const [isReady, setIsReady] = useState(initialState.isReady);
  const [activeChannel, setActiveChannel] = useState(initialState.activeChannel);
  const [messageCollectionList, setMessageCollectionList] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(initialState.isLoadingMessages);
  const [hasMoreMessages, setHasMoreMessages] = useState(initialState.hasMoreMessages);
  const [messages, setMessages] = useState(initialState.messages);
  const [quotedMessage, setQuotedMessage] = useState(initialState.quotedMessage);
  const [uploadProgress, setUploadProgress] = useState(initialState.uploadProgress);
  const [isUploading, setIsUploading] = useState(initialState.isUploading);
  const [isVideoReady, setIsVideoReady] = useState(initialState.isVideoReady);

  const { uploadAttachments, deleteAttachments } = useStorage();
  const [unreadCountDoc, setUnreadCountDoc] = useState(initialState.unreadCountDoc);
  const [scheduledCount, setScheduledCount] = useState(initialState.scheduledCount);
  const [scheduledMessagesCollectionList, setScheduledMessagesCollectionList] = useState([]);
  const [pendingScheduledCollection, setPendingScheduledCollection] = useState([]);
  const [pendingScheduledMessages, setPendingScheduledMessages] = useState([]);
  const [pendingScheduledCount, setPendingScheduledCount] = useState(0);
  const [scheduledMessages, setScheduledMessages] = useState(initialState.scheduledMessages);
  const [coachDoc, setCoachDoc] = useState(null);
  const [coachTimezone, setCoachTimezone] = useState(initialState.coachTimezone);
  const [smartContextText, setSmartContextText] = useState(initialState.smartContextText);
  const {
    scheduleQueue,
    scheduledMessageIds,
    handleAddToScheduleQueue,
    handleQueueTextChange,
    setScheduleQueue,
  } = useScheduleQueue(activeChannel?.id);

  const {
    activeChannelId,
    activeUserId,
    chatUser,
    authUser,
    setChannelError,
    channelError,
    setCurrentChatPage,
    setActiveUserId,
    setActiveChannelId,
    logEvent,
    sessionStore: { isCoachAssistant },
  } = useContext(ChatContext);

  const {
    firebase: {
      remote,
    },
  } = useContext(FirebaseContext);

  const { unreadCountsCollection, channelList } = useContext(ChatChannelListContext);
  const isComponentMountedRef = useComponentMounted();
  // Synchronous guard so overlapping scroll events can't fire concurrent pagination requests
  // (which would fetch and append the same page twice, producing duplicates).
  const isLoadingMoreRef = useRef(false);

  useEffect(() => {
    const initChannel = async () => {
      setIsReady(false);
      setChannelError(null);
      setActiveUserId('');
      setQuotedMessage(); // clear any stale quoted reply from the previous channel — no cross-channel replies
      setScheduledMessagesCollectionList([]);
      setPendingScheduledCollection([]);

      let channel = channelList.find((channelDoc) => channelDoc.id === activeChannelId);

      // Load the channel object from the database if it was not pre-fetched
      if (!channel) {
        channel = await ChatChannel.getById(activeChannelId);
      }

      // Get the all messages for the channel
      const channelMessagesCollection = await channel.getMessageList();

      if (isComponentMountedRef.current) {
        setActiveChannel(channel);
        setMessageCollectionList([channelMessagesCollection]);
        setHasMoreMessages(channelMessagesCollection.docs.length === CHANNEL_MESSAGE_PAGE_SIZE);
        setIsReady(true);
      }

      // Get all scheduled messages
      const scheduledMessagesCollection = await channel.getScheduledMessages(chatUser.id);

      // Get all pending scheduled messages
      const pendingScheduledMessageCol = await channel.getPendingScheduledMessages(chatUser.id);

      const coachDocument = await Coach.getById(chatUser.id);

      if (isComponentMountedRef.current) {
        setScheduledMessagesCollectionList([scheduledMessagesCollection]);
        setPendingScheduledCollection([pendingScheduledMessageCol]);
        setCoachDoc(coachDocument);
        setCoachTimezone(coachDocument.timeZone);
      }
    };

    if (activeChannelId && activeChannelId !== activeChannel?.id) {
      initChannel();
    }
  }, [
    activeChannelId,
    activeChannel,
    chatUser,
    isComponentMountedRef,
    setChannelError,
    setActiveUserId,
    setQuotedMessage,
    channelList,
  ]);

  /*
    This useEffect will set the active channel based on the activeUserId
    if no active channel is found for the activeUserId then it will set the
    channel error based on if chat is enabled for the user or not
  */
  useEffect(() => {
    const initChannel = async () => {
      setIsReady(false);
      setChannelError(null);
      setActiveChannelId('');
      setQuotedMessage(); // clear any stale quoted reply from the previous channel — no cross-channel replies

      // Load this user's channel with the coach in context: a reassigned client also keeps their old coach's channel
      const channel = await ChatChannel.getUserCoachChannel(activeUserId, chatUser.id);
      if (channel) {
        const channelMessagesCollection = await channel.getMessageList();
        const scheduledMessagesCollection = await channel.getScheduledMessages(chatUser.id);
        const coachDocument = await Coach.getById(chatUser.id);
        if (isComponentMountedRef.current) {
          setActiveChannel(channel);
          setMessageCollectionList([channelMessagesCollection]);
          setScheduledMessagesCollectionList([scheduledMessagesCollection]);
          setCurrentChatPage(ChatPage.SingleChannelView);
          setHasMoreMessages(channelMessagesCollection.docs.length === CHANNEL_MESSAGE_PAGE_SIZE);
          setCoachDoc(coachDocument);
          setCoachTimezone(coachDocument.timeZone);
          setIsReady(true);
        }
      } else {
        const isChatEnabledForUser = await isChatEnabled(activeUserId, chatUser.id);
        if (isComponentMountedRef.current) {
          if (!isChatEnabledForUser) {
            setChannelError(ChannelError.FEATURE_DISABLED);
          } else {
            setChannelError(ChannelError.CHANNEL_NOT_FOUND);
            Sentry.captureMessage('Chat channel not found for the current user', {
              extra: {
                userId: activeUserId,
              },
            });
          }
          setIsReady(true);
        }
      }
    };

    if (activeUserId && activeUserId !== activeChannel?.userId) {
      initChannel();
      setCurrentChatPage(ChatPage.SingleChannelView);
    }
  }, [
    activeUserId,
    activeChannel,
    isComponentMountedRef,
    setChannelError,
    setCurrentChatPage,
    setActiveChannelId,
    setQuotedMessage,
    chatUser.id,
  ]);

  /*
    This useEffect will set the message list when the messageCollectionList changes
  */
  useEffect(() => {
    const disposer = autorun(() => {
      const seenMessageIds = new Set();
      const messageDocList = [];
      messageCollectionList?.forEach((messageCollection) => {
        messageCollection.docs.forEach((messageDoc) => {
          // De-duplicate by id so an accidentally re-appended page can never render with the same key.
          if (!seenMessageIds.has(messageDoc.id)) {
            seenMessageIds.add(messageDoc.id);
            messageDocList.push(messageDoc);
          }
        });
      });
      setMessages(messageDocList);
    });

    return () => disposer();
  }, [
    messageCollectionList,
  ]);

  /*
    This useEffect will set the scheduled messages list when the scheduledMessagesList changes
  */
  useEffect(() => {
    const disposer = autorun(() => {
      const scheduledMessagesDocList = [];
      const pendingScheduledDocs = [];
      scheduledMessagesCollectionList?.forEach((scheduledMessageCollection) => {
        scheduledMessagesDocList.push(...scheduledMessageCollection.docs);
      });
      pendingScheduledCollection?.forEach((pendingScheduled) => {
        pendingScheduledDocs.push(...pendingScheduled.docs);
      });
      setScheduledMessages(scheduledMessagesDocList);
      setPendingScheduledMessages(pendingScheduledDocs);
    });

    return () => disposer();
  }, [
    scheduledMessagesCollectionList,
    pendingScheduledCollection,
    setScheduledMessages,
  ]);

  // This effect updates the unread count doc when the active channel changes or the unread count collection updates
  useEffect(() => {
    const disposer = autorun(() => {
      if (activeChannel) {
        const unreadDoc = unreadCountsCollection.docs.find((unreadCnt) => unreadCnt.channelId === activeChannel.id);
        setUnreadCountDoc(unreadDoc || initialState.unreadCountDoc);
      } else {
        setUnreadCountDoc(initialState.unreadCountDoc);
      }
    });

    return () => disposer();
  }, [
    unreadCountsCollection,
    activeChannel,
  ]);

  /*
    This useEffect will update the scheduled messages count everytime the channel or the messages change
  */
  useEffect(() => {
    setScheduledCount(scheduledMessages?.length);
    setPendingScheduledCount(pendingScheduledMessages?.length);
  }, [activeChannel, chatUser.id, messages, scheduledCount, scheduledMessages, pendingScheduledMessages]);

  const loadMoreMessages = useCallback(async () => {
    // Bail if a load is already in flight. This ref is synchronous so rapid scroll events can't start overlapping loads
    // that each append the same next page.
    if (isLoadingMoreRef.current || !activeChannel) {
      return;
    }
    isLoadingMoreRef.current = true;
    setIsLoadingMessages(true);
    try {
      const lastMessageCollection = messageCollectionList[messageCollectionList.length - 1];
      const nextMessageList = await activeChannel.getMessageList({
        startAfterDoc: lastMessageCollection.docs[lastMessageCollection.docs.length - 1],
      });

      if (isComponentMountedRef.current) {
        if (nextMessageList.docs.length < CHANNEL_MESSAGE_PAGE_SIZE) {
          setHasMoreMessages(false);
        }
        setMessageCollectionList((prevMessageList) => ([
          ...prevMessageList,
          nextMessageList,
        ]));
      }
    } finally {
      isLoadingMoreRef.current = false;
      if (isComponentMountedRef.current) {
        setIsLoadingMessages(false);
      }
    }
  }, [
    messageCollectionList,
    activeChannel,
    isComponentMountedRef,
  ]);

  // Resets the active channel state by clearing the active channel id, active user id, and active channel
  const resetChannelState = useCallback(() => {
    setActiveChannelId('');
    setActiveUserId('');
    setActiveChannel(initialState.activeChannel);
  }, [setActiveChannelId, setActiveUserId]);

  const onUploadProgress = useCallback((progress, fileId) => {
    setUploadProgress((prevProgress) => ({
      ...prevProgress,
      [fileId]: progress,
    }));
  }, []);

  const processAttachments = useCallback(async (attachments) => {
    const uploadedAttachments = [];

    const filesToAddById = attachments.reduce((acc, { file, id }) => {
      const lastDotIndex = file.name.lastIndexOf('.');
      acc[id] = {
        dataUrl: URL.createObjectURL(file),
        format: file.name.slice(lastDotIndex + 1),
        fileName: file.name.slice(0, lastDotIndex),
        fileType: file.type,
        fileId: id,
      };
      return acc;
    }, {});

    const filesToAdd = Object.values(filesToAddById);
    const storagePath = format(storagePaths.CHAT_COLLECTION, {
      [pathPlaceholder.CHANNEL_ID]: activeChannel.id,
    });
    setIsUploading(true);
    const results = await uploadAttachments(
      filesToAdd,
      storagePath,
      ({ progress, fileId }) => onUploadProgress(progress, fileId, filesToAdd.length),
    );

    const failedFiles = [];
    results.forEach((res) => {
      if (res.state === StorageProcessState.SUCCESS) {
        uploadedAttachments.push(res);
      } else {
        failedFiles.push(res);
      }
    });
    if (failedFiles.length > 0) {
      // If some upload fails, we should stop the sending process
      // and delete the files that were uploaded (if there is any)
      if (uploadedAttachments.length) {
        const filesToDelete = uploadedAttachments.map(({ fileRef }) => ({ storagePath: fileRef }));
        await deleteAttachments(filesToDelete);
      }
      const errorMessage = `Failed to upload ${failedFiles.length} file(s)`;
      setIsUploading(false);
      throw new Error(errorMessage);
    } else {
      setIsUploading(false);
      // format the attachment data to be saved in the message document
      return uploadedAttachments.map(({
        fileRef,
        metadata: {
          contentType,
          size,
        },
        fileId,
      }) => getProcessedAttachmentData({
        fileName: filesToAddById[fileId].fileName,
        contentType,
        pathRef: fileRef,
        fileSize: size,
      }));
    }
  }, [
    activeChannel,
    deleteAttachments,
    onUploadProgress,
    uploadAttachments,
  ]);

  const markChannelAsReadOnReply = useCallback(async (replySentAt) => {
    if (!(unreadCountDoc?.unreadCount > 0)) {
      return;
    }

    try {
      await unreadCountDoc.setUnreadCountToZero();
      logEvent(Event.CHAT_MARKED_AS_READ_ON_REPLY, {
        thread_id: activeChannel?.id || '',
        user_role: isCoachAssistant ? 'AC' : 'coach',
        reply_sent_at: replySentAt,
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          channelId: activeChannel.id,
          coachId: chatUser.id,
        },
      });
    }
  }, [unreadCountDoc, logEvent, activeChannel, isCoachAssistant, chatUser]);

  const sendMessage = useCallback(async (text, attachments = [], extras = {}) => {
    // upload the attachments and get the processed data
    const processedAttachments = await processAttachments(attachments);
    // Build the message object
    const message = {
      text,
      userId: chatUser.id,
      userName: chatUser.name,
      initiatedBy: authUser.id,
      initiatedByName: authUser.name,
      attachments: processedAttachments,
      ...extras,
    };

    // Send the message to the channel
    await activeChannel.sendMessage(message);

    logEvent(parentApp === 'web' ? EventType.COACHING_ACTIVITY : EventType.CLIENT_ACTIVITY, {
      coachId: chatUser.id,
      clientId: activeChannel.data.userId,
      initiatedBy: authUser.id,
      actionType: getChatActionType(text),
      actionTaken: ChatActivity.CHAT_MESSAGE_SENT,
    });

    await markChannelAsReadOnReply(new Date().toISOString());
  }, [
    chatUser,
    authUser,
    activeChannel,
    processAttachments,
    logEvent,
    parentApp,
    markChannelAsReadOnReply,
  ]);

  // Sends a message to a channel whose id the caller already holds, saving the channel lookup.
  const sendMessageToChannel = useCallback(
    async (
      channelId,
      text,
      processedAttachments,
      extras = {},
      shouldArchive = false,
    ) => {
      const unreadCount = await ChannelUnreadCount.getUnreadCountForChannelByUser(chatUser.id, channelId);
      const message = {
        text,
        userId: chatUser.id,
        userName: chatUser.name,
        initiatedBy: authUser.id,
        initiatedByName: authUser.name,
        attachments: processedAttachments,
        shouldArchive: unreadCount === 0 ? shouldArchive : false,
        ...extras,
      };

      await new ChatChannel(channelId).sendMessage(message);
    },
    [authUser.id, authUser.name, chatUser],
  );

  const sendMessageToUser = useCallback(
    async (
      toUserId,
      text,
      processedAttachments,
      extras = {},
      shouldArchive = false,
    ) => {
      const channelDoc = await ChatChannel.getUserCoachChannel(
        toUserId,
        chatUser.id,
      );
      if (channelDoc) {
        await sendMessageToChannel(channelDoc.id, text, processedAttachments, extras, shouldArchive);
      }
    },
    [chatUser, sendMessageToChannel],
  );

  const getUserChannelId = useCallback(async (userId) => {
    const channelDoc = await ChatChannel.getUserCoachChannel(userId, chatUser.id);

    if (channelDoc) {
      return channelDoc.id;
    }
    return '';
  }, [chatUser]);

  const scheduleMessage = useCallback(async (sendAt, text, attachments = [], shouldForceSend, extras = {}) => {
    // upload the attachments and get the processed data
    const processedAttachments = await processAttachments(attachments);
    // Build the message object
    const message = {
      text,
      userId: chatUser.id,
      initiatedBy: authUser.id,
      initiatedByName: authUser.name,
      attachments: processedAttachments,
      shouldForceSend,
      ...extras,
    };

    // Schedule the message to be sent to the channel
    await activeChannel.scheduleMessage(message, sendAt);

    await markChannelAsReadOnReply(sendAt.toISOString());
  }, [
    chatUser,
    authUser,
    activeChannel,
    processAttachments,
    markChannelAsReadOnReply,
  ]);

  const updateScheduledMessage = useCallback(async (
    messageRequestDoc, newSendAt, newText, newAttachments = [], status, shouldForceSend,
  ) => {
    // upload the attachments and get the processed data
    const processedNewAttachments = await processAttachments(newAttachments);

    // update the message request
    await messageRequestDoc.updateRequest(newText, processedNewAttachments, newSendAt, status, shouldForceSend);
  }, [
    processAttachments,
  ]);

  const deleteScheduledMessage = useCallback(async (messageRequestDoc) => {
    // delete the message request
    await messageRequestDoc.deleteRequest();

    // Restore the activity feed item if the message was scheduled for check-in feedback
    if (messageRequestDoc.activityId) {
      await remote('activityFeed-updateActivityStatus/restore', { activityIds: messageRequestDoc.activityId });
    }
  }, [
    remote,
  ]);

  // In ADMIN_COACHING channels thread replies live only in the thread modal — the feed hides them.
  const visibleMessages = useMemo(() => (activeChannel?.type === ChannelType.ADMIN_COACHING
    ? messages.filter((message) => !message.isThreadReply)
    : messages), [activeChannel, messages]);

  /*
    Filtering happens after the fetch, so a page can arrive and leave the feed looking almost empty.
    Waiting for a scroll doesn't rescue it: a page of pure thread replies changes neither the list nor
    its height, so the list never asks for more.
  */
  useEffect(() => {
    if (
      visibleMessages.length < MIN_VISIBLE_MESSAGES
      && hasMoreMessages
      && !isLoadingMoreRef.current
      && messageCollectionList.length > 0
    ) {
      loadMoreMessages();
    }
  }, [visibleMessages, hasMoreMessages, messageCollectionList.length, loadMoreMessages]);

  const {
    unseenThreadCount,
    unreadThreadRows,
    recentThreadRows,
    isUnreadTruncated,
    markThreadRead,
    markAllRead,
    isThreadInboxOpen,
    setIsThreadInboxOpen,
    threadNoticeRootId,
    setThreadNoticeRootId,
  } = useUnseenThreads(activeChannel, chatUser.id);

  const context = useMemo(() => ({
    isReady,
    setIsReady,
    activeChannel,
    setActiveChannel,
    sendMessage,
    sendMessageToUser,
    sendMessageToChannel,
    scheduleMessage,
    updateScheduledMessage,
    deleteScheduledMessage,
    messages,
    visibleMessages,
    loadMoreMessages,
    isLoadingMessages,
    setIsLoadingMessages,
    hasMoreMessages,
    unseenThreadCount,
    unreadThreadRows,
    recentThreadRows,
    isUnreadTruncated,
    markThreadRead,
    markAllRead,
    isThreadInboxOpen,
    setIsThreadInboxOpen,
    threadNoticeRootId,
    setThreadNoticeRootId,
    quotedMessage,
    setQuotedMessage,
    isUploading,
    uploadProgress,
    channelError,
    navigateToClientPage,
    renderHeaderExtras,
    unreadCountDoc,
    scheduledCount,
    setScheduledCount,
    isVideoReady,
    setIsVideoReady,
    scheduledMessages,
    getUserChannelId,
    pendingScheduledMessages,
    pendingScheduledCount,
    coachDoc,
    coachTimezone,
    resetChannelState,
    smartContextText,
    setSmartContextText,
    scheduleQueue,
    scheduledMessageIds,
    handleAddToScheduleQueue,
    handleQueueTextChange,
    setScheduleQueue,
  }), [
    isReady,
    setIsReady,
    activeChannel,
    setActiveChannel,
    sendMessage,
    sendMessageToUser,
    sendMessageToChannel,
    scheduleMessage,
    updateScheduledMessage,
    deleteScheduledMessage,
    messages,
    visibleMessages,
    loadMoreMessages,
    isLoadingMessages,
    setIsLoadingMessages,
    hasMoreMessages,
    unseenThreadCount,
    unreadThreadRows,
    recentThreadRows,
    isUnreadTruncated,
    markThreadRead,
    markAllRead,
    isThreadInboxOpen,
    setIsThreadInboxOpen,
    threadNoticeRootId,
    setThreadNoticeRootId,
    quotedMessage,
    setQuotedMessage,
    isUploading,
    uploadProgress,
    channelError,
    navigateToClientPage,
    renderHeaderExtras,
    unreadCountDoc,
    scheduledCount,
    setScheduledCount,
    isVideoReady,
    setIsVideoReady,
    scheduledMessages,
    getUserChannelId,
    pendingScheduledMessages,
    pendingScheduledCount,
    coachDoc,
    coachTimezone,
    resetChannelState,
    smartContextText,
    setSmartContextText,
    scheduleQueue,
    scheduledMessageIds,
    handleAddToScheduleQueue,
    handleQueueTextChange,
    setScheduleQueue,
  ]);

  return (
    <ChatChannelContext.Provider value={context}>
      {children}
    </ChatChannelContext.Provider>
  );
};

ChatChannelContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  navigateToClientPage: PropTypes.func,
  renderHeaderExtras: PropTypes.func,
  parentApp: PropTypes.oneOf(['web', 'mobile']).isRequired,
};

ChatChannelContextProvider.defaultProps = {
  navigateToClientPage: () => {},
  renderHeaderExtras: () => {},
};

export default compose(
  observer,
)(ChatChannelContextProvider);
